import React, { useMemo } from 'react';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Star } from 'lucide-react';
import { formatDate } from '../../lib/utils';

/**
 * Per-variant vendor pricing (2026-02).
 * Renders one row per vendor linked at the ITEM level, letting the operator set the
 * vendor SKU, price and lead time for THIS specific variant × vendor combination.
 * A Small polo may be $22 at Quantum vs a 2XL at $28 — this is where that lives.
 */
export function VariantVendorPricing({ item, variant, variantIndex, canEdit, onChange }) {
  const itemLinks = item.vendor_links || [];
  const preferredId = item.preferred_vendor_id || '';
  const variantLinks = variant.vendor_links || [];

  const vendors = item._vendorsById || {};  // opt-in helper passed by parent

  // Build a merged view — one row per item-linked vendor. If variant has no link for
  // that vendor yet, show empty inputs; typing seeds a new entry into variant.vendor_links.
  const rows = useMemo(() => {
    return itemLinks.map((il) => {
      const existing = variantLinks.find(vl => vl.vendor_id === il.vendor_id);
      return {
        vendor_id: il.vendor_id,
        is_preferred: il.vendor_id === preferredId,
        vendor_sku: existing?.vendor_sku ?? '',
        price: existing?.price ?? '',
        lead_time_days: existing?.lead_time_days ?? '',
        last_purchased_at: existing?.last_purchased_at ?? null,
      };
    });
  }, [itemLinks, variantLinks, preferredId]);

  const updateField = (vendorId, field, rawValue) => {
    let value = rawValue;
    if (field === 'price') value = rawValue === '' ? null : parseFloat(rawValue);
    else if (field === 'lead_time_days') value = rawValue === '' ? null : parseInt(rawValue, 10);
    else value = rawValue === '' ? null : rawValue;

    const next = [...variantLinks];
    const idx = next.findIndex(l => l.vendor_id === vendorId);
    if (idx >= 0) {
      next[idx] = { ...next[idx], [field]: value };
    } else {
      next.push({
        vendor_id: vendorId,
        vendor_sku: field === 'vendor_sku' ? value : null,
        price: field === 'price' ? value : null,
        lead_time_days: field === 'lead_time_days' ? value : null,
        last_purchased_at: null,
      });
    }
    onChange(variantIndex, 'vendor_links', next);
  };

  if (itemLinks.length === 0) {
    return (
      <div className="px-6 py-3 pl-12 bg-slate-50/50 text-xs text-slate-500 italic border-t">
        Add vendors on the Details tab to set per-size SKU and pricing here.
      </div>
    );
  }

  return (
    <div className="border-t bg-surface" data-testid={`variant-vendor-pricing-${variantIndex}`}>
      <div className="px-6 py-2 pl-12 text-xs font-semibold text-slate-500 uppercase tracking-wide bg-slate-50/50 border-b">
        Vendor Pricing
      </div>
      <div className="divide-y">
        {rows.map((r) => {
          const vName = vendors[r.vendor_id]?.name || 'Unknown vendor';
          return (
            <div
              key={r.vendor_id}
              className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.2fr)] gap-3 items-center px-6 py-2 pl-12"
              data-testid={`variant-${variantIndex}-vendor-${r.vendor_id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-medium text-sm truncate">{vName}</span>
                {r.is_preferred && (
                  <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 gap-1 h-5 text-[10px] px-1.5">
                    <Star className="w-2.5 h-2.5" /> Preferred
                  </Badge>
                )}
              </div>
              <Input
                placeholder="Vendor SKU"
                value={r.vendor_sku || ''}
                onChange={(e) => updateField(r.vendor_id, 'vendor_sku', e.target.value)}
                className="h-7 text-xs"
                readOnly={!canEdit}
                disabled={!canEdit}
                data-testid={`variant-${variantIndex}-vendor-${r.vendor_id}-sku`}
              />
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-slate-400">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Price"
                  value={r.price === null || r.price === undefined ? '' : r.price}
                  onChange={(e) => updateField(r.vendor_id, 'price', e.target.value)}
                  className="h-7 text-xs"
                  readOnly={!canEdit}
                  disabled={!canEdit}
                  data-testid={`variant-${variantIndex}-vendor-${r.vendor_id}-price`}
                />
              </div>
              <Input
                type="number"
                min="0"
                placeholder="Lead days"
                value={r.lead_time_days === null || r.lead_time_days === undefined ? '' : r.lead_time_days}
                onChange={(e) => updateField(r.vendor_id, 'lead_time_days', e.target.value)}
                className="h-7 text-xs"
                readOnly={!canEdit}
                disabled={!canEdit}
                data-testid={`variant-${variantIndex}-vendor-${r.vendor_id}-lead`}
              />
              <div className="text-xs text-slate-400 truncate">
                {r.last_purchased_at ? `Last: ${formatDate(r.last_purchased_at)}` : '—'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
